import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  ChevronLeft,
  ChevronRight,
  Download,
  ScrollText,
  X,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Skeleton } from '@/components/ui/Skeleton';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { Input } from '@/components/ui/Input';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/Avatar';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/Table';
import { AuditEventSheet } from '@/components/users/AuditEventSheet';
import { formatDateTime, formatRelative } from '@/lib/formatters';
import { ROLE_LABELS, type Role } from '@/lib/permissions';
import { cn } from '@/lib/utils';
import {
  AUDIT_EXPORT_MAX_ROWS,
  AUDIT_SYSTEM_ACTOR,
  fetchAuditLogsForExport,
  useAuditLogFacets,
  useAuditLogSearch,
  useProfiles,
  type AuditLog,
  type AuditLogQuery,
  type AuditSort,
  type AuditSortColumn,
} from '@/features/users/hooks';
import {
  ACTION_VARIANT,
  DEFAULT_AUDIT_FILTERS,
  actionLabel,
  actorDisplayName,
  actorEmail,
  actorInitials,
  auditRowsToCsv,
  changedFields,
  entityDisplayName,
  entityTypeLabel,
  isSystemActor,
  type AuditActorProfile,
  type AuditFilters,
} from '@/features/users/auditLog';

const ALL = 'all';
const PAGE_SIZES = [25, 50, 100, 250];

interface AuditLogExplorerProps {
  /** Pin the explorer to one person (employee Activity tab). Hides the user filter. */
  lockedActorId?: string;
  /** Controlled filter state (the page syncs it to the URL). */
  filters: AuditFilters;
  onFiltersChange: (next: AuditFilters) => void;
  title?: string;
  /** Rendered in the card header, e.g. an "Open in Audit log" link. */
  headerActions?: React.ReactNode;
}

export function AuditLogExplorer({
  lockedActorId,
  filters,
  onFiltersChange,
  title = 'Events',
  headerActions,
}: AuditLogExplorerProps) {
  const [selected, setSelected] = useState<AuditLog | null>(null);
  const [exporting, setExporting] = useState(false);

  const patch = (p: Partial<AuditFilters>) => onFiltersChange({ ...filters, ...p });

  // Debounce the search box so we don't hit Postgres on every keystroke.
  const [searchInput, setSearchInput] = useState(filters.search);
  useEffect(() => setSearchInput(filters.search), [filters.search]);
  useEffect(() => {
    if (searchInput === filters.search) return;
    const t = setTimeout(() => patch({ search: searchInput, page: 0 }), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  const query: AuditLogQuery = useMemo(
    () => ({
      search: filters.search || undefined,
      entityType: filters.entityType || undefined,
      entityId: filters.entityId || undefined,
      action: filters.action || undefined,
      actorId: lockedActorId ?? (filters.actorId || undefined),
      from: filters.from || undefined,
      to: filters.to || undefined,
      sort: filters.sort,
      page: filters.page,
      pageSize: filters.pageSize,
    }),
    [filters, lockedActorId],
  );

  const { data, isLoading, isFetching, error } = useAuditLogSearch(query);
  const { data: facets } = useAuditLogFacets();
  const { data: profiles } = useProfiles();

  const profilesById = useMemo(() => {
    const m = new Map<string, AuditActorProfile>();
    for (const p of profiles ?? []) m.set(p.id, p);
    return m;
  }, [profiles]);

  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / filters.pageSize));
  const firstIndex = total === 0 ? 0 : filters.page * filters.pageSize + 1;
  const lastIndex = Math.min(total, (filters.page + 1) * filters.pageSize);

  // If a filter change shrinks the result set below the current page, snap back.
  useEffect(() => {
    if (data && filters.page > 0 && filters.page >= pageCount) patch({ page: pageCount - 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, pageCount]);

  const hasFilters = Boolean(
    filters.search ||
    filters.entityType ||
    filters.entityId ||
    filters.action ||
    (!lockedActorId && filters.actorId) ||
    filters.from ||
    filters.to,
  );
  const clearFilters = () =>
    onFiltersChange({
      ...DEFAULT_AUDIT_FILTERS,
      sort: filters.sort,
      pageSize: filters.pageSize,
    });

  const toggleSort = (column: AuditSortColumn) => {
    const dir: AuditSort['dir'] =
      filters.sort.column === column
        ? filters.sort.dir === 'asc'
          ? 'desc'
          : 'asc'
        : column === 'created_at'
          ? 'desc'
          : 'asc';
    patch({ sort: { column, dir }, page: 0 });
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const all = await fetchAuditLogsForExport(query);
      const csv = auditRowsToCsv(all, profilesById);
      const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit-log-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      if (all.length >= AUDIT_EXPORT_MAX_ROWS) {
        toast.warning(
          `Export capped at ${AUDIT_EXPORT_MAX_ROWS.toLocaleString()} rows. Narrow the date range to export the rest.`,
        );
      } else {
        toast.success(`Exported ${all.length.toLocaleString()} events.`);
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const entityIdRow = filters.entityId
    ? rows.find((r) => r.entity_id === filters.entityId)
    : undefined;
  const entityIdLabel = (entityIdRow && entityDisplayName(entityIdRow)) || filters.entityId;

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2 space-y-0">
          <CardTitle>{title}</CardTitle>
          <div className="flex items-center gap-2">
            {headerActions}
            <Button
              variant="outline"
              size="sm"
              onClick={exportCsv}
              disabled={exporting || total === 0}
            >
              <Download className="h-3.5 w-3.5" />
              {exporting ? 'Exporting…' : 'Export CSV'}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <Input
              placeholder={
                lockedActorId ? 'Search entity, record, IP…' : 'Search user, entity, record, IP…'
              }
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="w-full sm:w-64"
            />
            <Select
              value={filters.entityType || ALL}
              onValueChange={(v) => patch({ entityType: v === ALL ? '' : v, page: 0 })}
            >
              <SelectTrigger className="w-full sm:w-52">
                <SelectValue placeholder="Entity" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All entities</SelectItem>
                {(facets?.entity_types ?? []).map((t) => (
                  <SelectItem key={t} value={t}>
                    {entityTypeLabel(t)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={filters.action || ALL}
              onValueChange={(v) => patch({ action: v === ALL ? '' : v, page: 0 })}
            >
              <SelectTrigger className="w-full sm:w-40">
                <SelectValue placeholder="Action" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All actions</SelectItem>
                {orderActions(facets?.actions ?? []).map((a) => (
                  <SelectItem key={a} value={a}>
                    {actionLabel(a)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {!lockedActorId && (
              <Select
                value={filters.actorId || ALL}
                onValueChange={(v) => patch({ actorId: v === ALL ? '' : v, page: 0 })}
              >
                <SelectTrigger className="w-full sm:w-52">
                  <SelectValue placeholder="User" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL}>All users</SelectItem>
                  {facets?.has_system && (
                    <SelectItem value={AUDIT_SYSTEM_ACTOR}>System (automated)</SelectItem>
                  )}
                  {(facets?.actors ?? []).map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <div className="flex items-center gap-1.5">
              <Input
                type="date"
                aria-label="From date"
                value={filters.from}
                max={filters.to || undefined}
                onChange={(e) => patch({ from: e.target.value, page: 0 })}
                className="w-40"
              />
              <span className="text-xs text-muted-foreground">to</span>
              <Input
                type="date"
                aria-label="To date"
                value={filters.to}
                min={filters.from || undefined}
                onChange={(e) => patch({ to: e.target.value, page: 0 })}
                className="w-40"
              />
            </div>
            {hasFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                <X className="h-3.5 w-3.5" />
                Clear
              </Button>
            )}
          </div>

          {filters.entityId && (
            <div className="mb-3 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
              <span>Showing history of</span>
              <Badge variant="info-soft" className="gap-1 font-mono">
                {entityIdLabel}
                <button
                  type="button"
                  aria-label="Clear record filter"
                  className="ml-0.5 rounded-full hover:text-foreground"
                  onClick={() => patch({ entityId: '', page: 0 })}
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            </div>
          )}

          {error ? (
            <EmptyState
              icon={ScrollText}
              title="Could not load the audit log"
              detail={error instanceof Error ? error.message : 'Unknown error'}
            />
          ) : isLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          ) : rows.length === 0 ? (
            <EmptyState
              icon={ScrollText}
              title={
                hasFilters
                  ? 'No events match these filters'
                  : lockedActorId
                    ? 'No recorded activity for this person'
                    : 'No audit events yet'
              }
            />
          ) : (
            <div className={cn(isFetching && 'opacity-60 transition-opacity')}>
              <Table>
                <TableHeader>
                  <TableRow>
                    <SortableHead
                      column="created_at"
                      sort={filters.sort}
                      onSort={toggleSort}
                      className="whitespace-nowrap"
                    >
                      When
                    </SortableHead>
                    {!lockedActorId && (
                      <SortableHead column="actor_name" sort={filters.sort} onSort={toggleSort}>
                        Who
                      </SortableHead>
                    )}
                    <SortableHead column="action" sort={filters.sort} onSort={toggleSort}>
                      Action
                    </SortableHead>
                    <SortableHead column="entity_type" sort={filters.sort} onSort={toggleSort}>
                      Entity
                    </SortableHead>
                    <TableHead>Changes</TableHead>
                    <TableHead className="whitespace-nowrap">IP</TableHead>
                    <TableHead className="w-8" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((row) => (
                    <AuditRow
                      key={row.id}
                      row={row}
                      actor={row.actor_id ? profilesById.get(row.actor_id) : undefined}
                      hideActor={!!lockedActorId}
                      onOpen={() => setSelected(row)}
                    />
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {!isLoading && !error && total > 0 && (
            <div className="mt-4 flex flex-wrap items-center justify-between gap-3 text-xs text-muted-foreground">
              <span>
                Showing {firstIndex.toLocaleString()}–{lastIndex.toLocaleString()} of{' '}
                {total.toLocaleString()} event{total === 1 ? '' : 's'}
              </span>
              <div className="flex items-center gap-2">
                <Select
                  value={String(filters.pageSize)}
                  onValueChange={(v) => patch({ pageSize: Number(v), page: 0 })}
                >
                  <SelectTrigger className="h-8 w-28 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PAGE_SIZES.map((n) => (
                      <SelectItem key={n} value={String(n)}>
                        {n} per page
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={filters.page === 0}
                  onClick={() => patch({ page: filters.page - 1 })}
                  aria-label="Previous page"
                >
                  <ChevronLeft className="h-3.5 w-3.5" />
                </Button>
                <span className="tabular-nums">
                  Page {filters.page + 1} of {pageCount.toLocaleString()}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={filters.page + 1 >= pageCount}
                  onClick={() => patch({ page: filters.page + 1 })}
                  aria-label="Next page"
                >
                  <ChevronRight className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <AuditEventSheet
        row={selected}
        actor={selected?.actor_id ? profilesById.get(selected.actor_id) : undefined}
        onClose={() => setSelected(null)}
        onFilterActor={
          lockedActorId
            ? undefined
            : (actorId) => {
                setSelected(null);
                patch({ actorId, entityId: '', page: 0 });
              }
        }
        onFilterEntity={(entityType, entityId) => {
          setSelected(null);
          patch({ entityType, entityId, page: 0 });
        }}
      />
    </>
  );
}

function orderActions(actions: string[]): string[] {
  const core = ['insert', 'update', 'delete'].filter((a) => actions.includes(a));
  const rest = actions.filter((a) => !core.includes(a)).sort();
  return [...core, ...rest];
}

function SortableHead({
  column,
  sort,
  onSort,
  className,
  children,
}: {
  column: AuditSortColumn;
  sort: AuditSort;
  onSort: (c: AuditSortColumn) => void;
  className?: string;
  children: React.ReactNode;
}) {
  const active = sort.column === column;
  const Icon = !active ? ArrowUpDown : sort.dir === 'asc' ? ArrowUp : ArrowDown;
  return (
    <TableHead className={className}>
      <button
        type="button"
        onClick={() => onSort(column)}
        className={cn(
          'inline-flex items-center gap-1 rounded hover:text-foreground',
          active && 'text-foreground',
        )}
        aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
      >
        {children}
        <Icon className={cn('h-3 w-3', !active && 'opacity-50')} />
      </button>
    </TableHead>
  );
}

const MAX_INLINE_FIELDS = 4;

function AuditRow({
  row,
  actor,
  hideActor,
  onOpen,
}: {
  row: AuditLog;
  actor: AuditActorProfile | undefined;
  hideActor: boolean;
  onOpen: () => void;
}) {
  const fields = row.action === 'update' ? changedFields(row) : [];
  const entityName = entityDisplayName(row);
  const roleLabel = row.actor_role ? (ROLE_LABELS[row.actor_role as Role] ?? row.actor_role) : null;
  return (
    <TableRow
      className="cursor-pointer"
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen();
        }
      }}
      tabIndex={0}
      role="button"
      aria-label={`Open audit event ${row.id}`}
    >
      <TableCell className="whitespace-nowrap">
        <p className="text-foreground">{formatDateTime(row.created_at, { seconds: true })}</p>
        <p className="text-xs text-muted-foreground">{formatRelative(row.created_at)}</p>
      </TableCell>
      {!hideActor && (
        <TableCell>
          <div className="flex items-center gap-2.5">
            <Avatar className="h-7 w-7">
              {actor?.avatar_url && <AvatarImage src={actor.avatar_url} alt="" />}
              <AvatarFallback className="text-[10px]">{actorInitials(row, actor)}</AvatarFallback>
            </Avatar>
            <div className="min-w-0">
              <p className="truncate text-sm font-medium text-foreground">
                {actorDisplayName(row, actor)}
                {roleLabel && (
                  <span className="ml-1.5 text-xs font-normal text-muted-foreground">
                    · {roleLabel}
                  </span>
                )}
              </p>
              <p className="truncate text-xs text-muted-foreground">
                {actorEmail(row, actor) ??
                  (isSystemActor(row) ? 'Automated' : (row.actor_id?.slice(0, 8) ?? '') + '…')}
              </p>
            </div>
          </div>
        </TableCell>
      )}
      <TableCell>
        <Badge variant={ACTION_VARIANT[row.action] ?? 'muted'}>{actionLabel(row.action)}</Badge>
      </TableCell>
      <TableCell>
        <p className="font-medium text-foreground">{entityTypeLabel(row.entity_type)}</p>
        <p
          className="truncate font-mono text-xs text-muted-foreground"
          title={row.entity_id ?? undefined}
        >
          {entityName ?? row.entity_id ?? '—'}
        </p>
      </TableCell>
      <TableCell className="text-xs text-muted-foreground">
        {row.action === 'update' ? (
          fields.length === 0 ? (
            <span>No field changes</span>
          ) : (
            <span className="font-mono">
              {fields.slice(0, MAX_INLINE_FIELDS).join(', ')}
              {fields.length > MAX_INLINE_FIELDS && ` +${fields.length - MAX_INLINE_FIELDS} more`}
            </span>
          )
        ) : row.action === 'insert' ? (
          <span>New record</span>
        ) : row.action === 'delete' ? (
          <span>Record removed</span>
        ) : (
          <span>—</span>
        )}
      </TableCell>
      <TableCell className="whitespace-nowrap font-mono text-xs text-muted-foreground">
        {row.ip_address ?? '—'}
        {row.auth_aal === 'aal2' && (
          <span className="ml-1.5 font-sans text-[10px] text-bright-moss-dark" title="MFA verified">
            MFA
          </span>
        )}
      </TableCell>
      <TableCell className="text-muted-foreground">
        <ChevronRight className="h-4 w-4" />
      </TableCell>
    </TableRow>
  );
}
